import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { allPageFaqs } from "@/lib/section-faqs";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_DRUG_PAGES, drugRefs } from "@/lib/drug-schema";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Qué medicamentos para bajar de peso receta el Dr. Fitzer?",
    a: "Recetamos toda la gama: semaglutida (Wegovy®, Ozempic®), tirzepatida (Zepbound®, Mounjaro®) y los medicamentos orales como fentermina, Contrave®, orlistat y dietilpropión. Cuál le conviene depende de su historia médica, su seguro y sus metas.",
  },
  {
    q: "¿Cuánto peso puedo esperar perder?",
    a: "En los estudios clínicos, la semaglutida logró una pérdida promedio cercana al 15 por ciento del peso corporal y la tirzepatida algo más. Los resultados individuales varían mucho y dependen en gran medida de la alimentación y la actividad física que acompañen al medicamento.",
  },
  {
    q: "¿Tengo que tomar el medicamento para siempre?",
    a: "Para la mayoría de los pacientes, sí, o al menos durante mucho tiempo. La obesidad se comporta como una enfermedad crónica. Cuando se suspende el tratamiento, buena parte del peso regresa.",
  },
  {
    q: "¿Los cubre el seguro médico en Virginia?",
    a: "Depende por completo de su plan. Algunos planes de Virginia cubren el control de peso por completo y otros lo excluyen. Verificamos sus beneficios específicos antes de que usted se comprometa a nada.",
  },
  {
    q: "¿Cuáles son los efectos secundarios más comunes?",
    a: "Náuseas, diarrea, estreñimiento y vómito. Casi todos son de leves a moderados y se calman a medida que el cuerpo se adapta. Empezar con dosis baja y subir despacio es lo que los mantiene tolerables.",
  },
  {
    q: "¿Puedo usar medicamentos después de una cirugía bariátrica?",
    a: "Sí. El uso de medicamentos después de la cirugía es cada vez más común, sobre todo en pacientes que recuperaron peso o que no llegaron a su meta. Los dos tratamientos funcionan bien juntos.",
  },
];

export const Route = createFileRoute("/es/medicamentos-para-bajar-de-peso")({
  head: () => {
    const base = esHead("medications");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              { ...esWebPageSchema("medications"), mentions: ES_DRUG_PAGES.flatMap(drugRefs) },
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/medicamentos-para-bajar-de-peso#faq`,
                inLanguage: "es-US",
                // Preguntas visibles del bloque de FAQ más los encabezados
                // de sección en forma de pregunta.
                mainEntity: allPageFaqs("/es/medicamentos-para-bajar-de-peso", FAQS).map((f, i) => ({
                  "@type": "Question",
                  ...(i < FAQS.length ? { "@id": `${SITE}/es/medicamentos-para-bajar-de-peso#faq-${i + 1}` } : {}),
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "MedicalTherapy",
                "@id": `${SITE}/es/medicamentos-para-bajar-de-peso#tratamiento`,
                name: "Tratamiento con medicamentos para bajar de peso (GLP-1 y GIP/GLP-1)",
                description:
                  "Medicamentos contra la obesidad aprobados por la FDA, usados junto con alimentación, actividad física y apoyo conductual para el control del peso a largo plazo.",
                url: `${SITE}/es/medicamentos-para-bajar-de-peso`,
                adverseOutcome: {
                  "@type": "MedicalEntity",
                  name: "Efectos secundarios digestivos: náuseas, diarrea, estreñimiento y vómito",
                },
                howPerformed:
                  "El tratamiento comienza con una dosis baja que se aumenta poco a poco durante varias semanas, con seguimiento del peso, la tolerancia y los marcadores metabólicos.",
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsMedicationsGuide,
});

function EsMedicationsGuide() {
  return (
    <EsPageShell
      page="medications"
      title="Medicamentos para bajar de peso (GLP-1 y más) en el Norte de Virginia"
      introExtra="El Dr. Fitzer receta y maneja estos medicamentos en nuestro consultorio de 171 Elden Street, Suite 3B, en Herndon, con seguimiento por telesalud disponible."
      intro="Los medicamentos para bajar de peso han cambiado mucho en los últimos años. Las inyecciones nuevas funcionan mucho mejor que todo lo anterior, y también traen requisitos y riesgos reales. Esta página explica las opciones principales, quién califica, qué esperar y qué debe saber sobre la seguridad antes de empezar."
    >
      <EsSection id="opciones" heading="Las principales opciones">
        <p>
          Varios medicamentos están aprobados por la FDA para el control del peso a largo plazo.
          Se diferencian en cómo funcionan, cómo se toman y cuánto peso suelen quitar.
        </p>

        <h3 className="mt-4 font-serif text-xl text-brand-mid">Semaglutida (Wegovy, Ozempic)</h3>
        <p>
          La{" "}
          <Link to="/es/semaglutida" className="font-medium text-brand-logo hover:underline">
            semaglutida
          </Link>{" "}
          es una inyección semanal que imita una hormona intestinal natural llamada péptido similar
          al glucagón tipo 1, o GLP-1. Esa hormona hace tres cosas útiles: le avisa al cerebro que
          usted ya está satisfecho, hace más lento el vaciamiento del estómago y ayuda a estabilizar
          el azúcar en la sangre. Juntos, esos efectos hacen mucho más fácil comer menos sin vivir a
          base de fuerza de voluntad.
        </p>

        <h3 className="mt-4 font-serif text-xl text-brand-mid">
          Tirzepatida (Zepbound, Mounjaro)
        </h3>
        <p>
          La{" "}
          <Link to="/es/tirzepatida" className="font-medium text-brand-logo hover:underline">
            tirzepatida
          </Link>{" "}
          también es una inyección semanal; se vende como Zepbound para el control del peso y como
          Mounjaro para la diabetes. Actúa sobre dos vías en lugar de una: activa a la vez los
          receptores de GIP y de GLP-1. En los estudios, esa acción doble produjo más pérdida de
          peso que el GLP-1 solo.
        </p>
        <p>
          Zepbound además tiene una aprobación de la FDA que ningún otro medicamento para bajar de
          peso tiene: el tratamiento de la apnea obstructiva del sueño de moderada a grave en
          adultos con obesidad. Si usted tiene obesidad y apnea del sueño, eso importa.
        </p>

        <h3 className="mt-4 font-serif text-xl text-brand-mid">
          Medicamentos orales y opciones más antiguas
        </h3>
        <p>
          La fentermina, Contrave, el orlistat y el dietilpropión se siguen usando. Estos{" "}
          <Link
            to="/es/medicamentos-orales-para-bajar-de-peso"
            className="font-medium text-brand-logo hover:underline"
          >
            medicamentos orales para bajar de peso
          </Link>{" "}
          cuestan mucho menos que las inyecciones y, en promedio, producen menos pérdida de peso.
          Para algunos pacientes, sobre todo quienes pagan de su bolsillo, son el lugar correcto
          para empezar.
        </p>
      </EsSection>

      <EsSection id="candidatos" heading="¿Quién es candidato?">
        <p>
          Estos medicamentos están aprobados para adultos con un índice de masa corporal (IMC) de 30
          o más. Un IMC de 27 o más también califica si usted tiene una enfermedad relacionada con
          el peso, como diabetes tipo 2, presión alta o apnea del sueño.
        </p>
        <p>
          El IMC es apenas el punto de partida. Cada medicamento tiene sus propias precauciones y
          algunas dependen de sus antecedentes personales y familiares. Repasamos esa historia con
          usted antes de recetar cualquier cosa.
        </p>
      </EsSection>

      <EsSection id="que-esperar" heading="Qué esperar durante el tratamiento">
        <p>
          Se empieza con una dosis baja que sube poco a poco a lo largo de varias semanas. Ese
          aumento lento es intencional y es lo que mantiene manejables los efectos secundarios.
        </p>
        <p>
          Casi todos los efectos secundarios son digestivos. Náuseas, diarrea, estreñimiento y
          vómito son los comunes, y suelen ser de leves a moderados. Tienden a ceder a medida que el
          cuerpo se adapta.
        </p>
        <p>
          Vale la pena aclarar algo desde el principio: estos medicamentos tratan la obesidad como
          la enfermedad continua que es. La investigación muestra de forma constante que buena parte
          del peso perdido regresa cuando se suspende el tratamiento. Piense en esto como una
          atención de largo plazo, no como un ciclo de antibióticos.
        </p>
      </EsSection>

      <EsSection id="seguridad" heading="Seguridad: lo que debe saber antes de empezar">
        <p>
          Son medicamentos eficaces y con un perfil de seguridad bien documentado. También traen
          advertencias que conviene entender antes de la primera inyección, no después.
        </p>

        <h3 className="mt-4 font-serif text-xl text-brand-mid">
          La advertencia sobre tumores de tiroides
        </h3>
        <p>
          La semaglutida y la tirzepatida llevan una advertencia en recuadro, la más fuerte que
          emite la FDA. En estudios, estos medicamentos causaron tumores de células C de la tiroides
          en ratas. No se sabe si hacen lo mismo en las personas: los investigadores no han podido
          determinar si el hallazgo en ratas aplica a los seres humanos, y la advertencia refleja esa
          incertidumbre más que un daño comprobado.
        </p>
        <p>
          Avísenos de inmediato si nota un bulto o una hinchazón en el cuello, ronquera que no se
          quita, dificultad para tragar o falta de aire.
        </p>

        <h3 className="mt-4 font-serif text-xl text-brand-mid">Quiénes no deben tomarlos</h3>
        <p>
          No tome semaglutida ni tirzepatida si usted o un familiar ha tenido carcinoma medular de
          tiroides. Lo mismo aplica si usted tiene el síndrome de neoplasia endocrina múltiple tipo
          2 (MEN 2), una condición hereditaria que aumenta el riesgo de ciertos tumores endocrinos.
          Por eso preguntamos por sus antecedentes familiares con más detalle del que quizá espera.
        </p>
        <p>
          Tampoco debe tomarlos si antes tuvo una reacción alérgica grave a estos medicamentos.
        </p>

        <h3 className="mt-4 font-serif text-xl text-brand-mid">Reacciones alérgicas y otros riesgos</h3>
        <p>
          Desde que estos medicamentos salieron al mercado se han reportado reacciones alérgicas
          graves, incluida la anafilaxia y la hinchazón de la cara o la garganta. Si se le hincha la
          cara o la garganta, o si le cuesta respirar, suspenda el medicamento y busque ayuda de
          inmediato.
        </p>
        <p>
          También vigilamos la pancreatitis, la enfermedad de la vesícula y los problemas de riñón
          por deshidratación. Si usted tiene diabetes y usa insulina o una sulfonilurea, el azúcar
          puede bajarle demasiado, así que normalmente ajustamos esas dosis al empezar.
        </p>
        <p>
          Nada de esto es motivo para tenerle miedo al tratamiento. Es motivo para asegurarse de
          hacerlo con un buen médico.
        </p>
      </EsSection>

      <EsSection id="costo" heading="Costo y cobertura en Virginia">
        <p>
          Los medicamentos de marca son caros y la cobertura para el control del peso varía
          enormemente de un plan a otro. Algunos los cubren por completo, otros los excluyen y
          muchos exigen autorización previa con documentación de intentos anteriores de pérdida de
          peso. Verificamos sus beneficios antes de que usted se comprometa. Vea nuestra página de{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            cobertura de seguro
          </Link>{" "}
          para los requisitos plan por plan.
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="comparar" heading="Compare con las opciones quirúrgicas">
        <p>
          Si el medicamento no es suficiente o usted busca un resultado más duradero, conozca la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>
          , el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>{" "}
          y el{" "}
          <Link to="/es/balon-gastrico" className="font-medium text-brand-logo hover:underline">
            balón gástrico
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="mas-informacion" heading="Más información en español">
        <p className="flex flex-wrap justify-center gap-3 pt-1">
          <Link
            to="/es/quien-califica-para-medicamentos"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            ¿Quién califica?
          </Link>
          <Link
            to="/es/cobertura-de-seguro"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Cobertura de seguro
          </Link>
        </p>
      </EsSection>
    </EsPageShell>
  );
}
